import React, { Fragment, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  useTheme,
  Button,
  ClickAwayListener,
} from "@mui/material";
import { GridDeleteIcon, GridExpandMoreIcon } from "@mui/x-data-grid";
import {
  Add,
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  MoreVert,
} from "@mui/icons-material";
import KeyColor from "./keyColor";

export interface DatagridColumn {
  headerName: string;
  field: string;
  flex?: number;
  minWidth?: number;
  width?: number;
  myColumnType: string;
  headerAlign?: "left" | "center" | "right";
  colorsByKey?: Record<string, any>;
  [key: string]: any;
}

interface DatagridColumnItemProps {
  column: DatagridColumn;
  index: number;
  length: number;
  styles: any;
  handleUpdateColumns: (
    newColumns: DatagridColumn,
    columnIndex: number,
  ) => void;
  handleColumnDelete: (index: number) => void;
  handleColMoveUp: (index: number) => void;
  handleColMoveDown: (index: number) => void;
  getAllColors: () => any;
  getColorFromPallet: (index: number) => string;
}

export type ColorsByKey = [string, { text?: string; color: string } | string][];

const DatagridColumns: React.FC<DatagridColumnItemProps> = ({
  column,
  index,
  styles,
  length,
  handleColumnDelete,
  handleUpdateColumns,
  handleColMoveUp,
  handleColMoveDown,
  getAllColors,
  getColorFromPallet,
}) => {
  const theme = useTheme();
  const [columnData, setColumnData] = useState<DatagridColumn>({
    headerName: "",
    field: "",
    myColumnType: "",
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [openColorOptionIndex, setOpenColorOptionIndex] = useState<
    number | null
  >(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  useEffect(() => {
    if (column.colorsByKey) {
      if (
        typeof column.colorsByKey === "object" &&
        !Array.isArray(column.colorsByKey)
      ) {
        const newColors = Object.entries(column.colorsByKey);
        setColumnData({ ...column, colorsByKey: newColors });
      } else {
        setColumnData(column);
      }
    } else {
      setColumnData(column);
    }
  }, []);

  const getColumnDataForParent = (data: DatagridColumn) => {
    if (data.colorsByKey && Array.isArray(data.colorsByKey)) {
      return {
        ...data,
        colorsByKey: Object.fromEntries(data.colorsByKey),
      };
    }
    return data;
  };

  const handleUpdateKeyColor = (
    newKeyValue: [string, { text: string; color: string }],
    keyIndex: number,
  ) => {
    setColumnData((prev) => {
      const currentColors = Array.isArray(prev.colorsByKey)
        ? prev.colorsByKey
        : [];

      const newColorsByKey = [...currentColors];
      newColorsByKey[keyIndex] = newKeyValue;

      const updated = {
        ...prev,
        colorsByKey: newColorsByKey,
      };

      handleUpdateColumns(getColumnDataForParent(updated), index);
      return updated;
    });
  };

  const handleColumnDataChange = (key: keyof DatagridColumn, value: any) => {
    setColumnData((prev) => {
      let updated = { ...prev, [key]: value };
      if (key === "myColumnType") {
        updated = { ...updated, colorsByKey: {} };
      }

      handleUpdateColumns(getColumnDataForParent(updated), index);
      return updated;
    });
  };

  const handleAddColorKey = () => {
    setColumnData((prev) => {
      const currentColors = Array.isArray(prev.colorsByKey)
        ? prev.colorsByKey
        : [];

      if (
        columnData?.myColumnType === "chip" ||
        columnData?.myColumnType === "progress"
      ) {
        const existingKeys = currentColors.map(([key]) => key);
        let newKey = "1";
        for (let i = 1; ; i++) {
          if (!existingKeys.includes(String(i))) {
            newKey = String(i);
            break;
          }
        }
        const newColorsByKey = [
          ...currentColors,
          [newKey, getColorFromPallet(currentColors.length)],
        ];

        const updated = {
          ...prev,
          colorsByKey: newColorsByKey,
        };

        handleUpdateColumns(getColumnDataForParent(updated), index);
        return updated;
      }

      const existingKeys = currentColors.map(([key]) => key);
      let newKey = "1";
      for (let i = 1; ; i++) {
        if (!existingKeys.includes(String(i))) {
          newKey = String(i);
          break;
        }
      }
      const newColorsByKey = [
        ...currentColors,
        [newKey, { text: "", color: getColorFromPallet(currentColors.length) }],
      ];

      const updated = {
        ...prev,
        colorsByKey: newColorsByKey,
      };

      handleUpdateColumns(getColumnDataForParent(updated), index);
      return updated;
    });
  };
  const handleRemoveColorKey = (indexToRemove: number) => {
    setColumnData((prev) => {
      const currentColors = Array.isArray(prev.colorsByKey)
        ? prev.colorsByKey
        : [];

      const newColorsByKey = currentColors.filter(
        (_, index) => index !== indexToRemove,
      );

      const updated = {
        ...prev,
        colorsByKey: newColorsByKey,
      };
      handleUpdateColumns(getColumnDataForParent(updated), index);
      return updated;
    });
  };

  return (
    <Box>
      <Accordion
        defaultExpanded
        sx={{
          border: 1,
          borderColor: theme.palette.background.textField,
          background: theme.palette.background.default,
        }}
      >
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls="panel-config-grafico"
          id="panel-config-header"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography id="panel-config-title">{`Coluna ${
              index + 1
            }`}</Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleColMoveUp(index);
                }}
                disabled={index === 0}
              >
                <ArrowUpwardOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleColMoveDown(index);
                }}
                disabled={index === length - 1}
              >
                <ArrowDownwardOutlined fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuOpen(e);
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColumnDelete(index);
                    handleMenuClose();
                  }}
                >
                  <GridDeleteIcon fontSize="small" />
                  Remover
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={styles.accordionDetails}>
          <Box>
            <TextField
              sx={styles.datagridTextField}
              label="Campo"
              variant="filled"
              value={columnData?.field}
              onChange={(e) => handleColumnDataChange("field", e.target.value)}
            />
            <TextField
              sx={styles.datagridTextField}
              label="Nome da coluna"
              variant="filled"
              value={columnData?.headerName}
              onChange={(e) =>
                handleColumnDataChange("headerName", e.target.value)
              }
            />
            <TextField
              sx={styles.datagridTextField}
              select
              label="Tipo de coluna"
              variant="filled"
              value={columnData?.myColumnType}
              onChange={(e) =>
                handleColumnDataChange("myColumnType", e.target.value)
              }
            >
              <MenuItem value={"text"}>Text</MenuItem>
              <MenuItem value={"data"}>Data</MenuItem>
              <MenuItem value={"progress"}>Progress</MenuItem>
              <MenuItem value={"chip"}>Chip</MenuItem>
              <MenuItem value={"customText"}>Custom text</MenuItem>
              <MenuItem value={"money"}>Dinheiro</MenuItem>
            </TextField>
            <TextField
              sx={styles.datagridTextField}
              disabled={columnData?.flex ? true : false}
              label="Largura da Coluna"
              variant="filled"
              type="number"
              value={columnData?.width ? columnData.width : ""}
              onChange={(e) => handleColumnDataChange("width", e.target.value)}
            />
            <Box sx={styles.container}>
              <Typography sx={styles.container}>
                Alinhamento da coluna
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  background: theme.palette.background.paper,
                  borderRadius: 1,
                }}
              >
                <IconButton
                  sx={{
                    ...styles.alignButton,
                    borderColor:
                      columnData?.headerAlign === "left" ||
                      columnData?.headerAlign === undefined
                        ? theme.palette.cromatic.green.main
                        : "transparent",
                  }}
                  onClick={() => {
                    handleColumnDataChange("headerAlign", "left");
                    handleColumnDataChange("align", "left");
                  }}
                >
                  <Typography>Esquerda</Typography>
                </IconButton>
                <Box
                  sx={{
                    width: "1px",
                    background: theme.palette.background.textField,
                  }}
                />
                <IconButton
                  sx={{
                    ...styles.alignButton,
                    border: 2,
                    borderColor:
                      columnData?.headerAlign === "center"
                        ? theme.palette.cromatic.green.main
                        : "transparent",
                  }}
                  onClick={() => {
                    handleColumnDataChange("headerAlign", "center");
                    handleColumnDataChange("align", "center");
                  }}
                >
                  <Typography>Centro</Typography>
                </IconButton>
                <Box
                  sx={{
                    width: "1px",
                    background: theme.palette.background.textField,
                  }}
                />
                <IconButton
                  sx={{
                    ...styles.alignButton,
                    borderColor:
                      columnData?.headerAlign === "right"
                        ? theme.palette.cromatic.green.main
                        : "transparent",
                  }}
                  onClick={() => {
                    handleColumnDataChange("headerAlign", "right");
                    handleColumnDataChange("align", "right");
                  }}
                >
                  <Typography>Direita</Typography>
                </IconButton>
              </Box>
            </Box>
            <FormControl sx={styles.switchFormControl}>
              <FormLabel
                sx={{
                  color: theme.palette.text.primary,
                  "&.Mui-focused": {
                    color: theme.palette.text.primary,
                  },
                }}
              >
                Flex
              </FormLabel>
              <FormControlLabel
                value="bottom"
                control={
                  <Switch
                    sx={{
                      marginLeft: -1,
                    }}
                    checked={columnData?.flex ? true : false}
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleColumnDataChange("flex", 1);
                      } else {
                        handleColumnDataChange("flex", null);
                        handleColumnDataChange("minWidth", null);
                        ///  handleColumnDataFieldRemove("flex");
                        // handleColumnDataFieldRemove("minWidth");
                      }
                    }}
                  />
                }
                label="Adiciona largura Flexível a coluna"
                labelPlacement="top"
                sx={styles.switchLabel}
              />
            </FormControl>
            {columnData?.flex && (
              <TextField
                sx={styles.datagridTextField}
                label="Largura minima"
                variant="filled"
                type="number"
                value={columnData?.minWidth}
                onChange={(e) =>
                  handleColumnDataChange("minWidth", e.target.value)
                }
              />
            )}
          </Box>
          {(columnData?.myColumnType === "customText" ||
            columnData?.myColumnType === "chip" ||
            columnData?.myColumnType === "progress") && (
            <Box
              sx={{
                paddingTop: 1,
                borderTop: 1,
                borderColor: theme.palette.background.textField,
              }}
            >
              <Box
                sx={{
                  marginBottom: 2,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography>Cores</Typography>
                <Button
                  sx={{ color: theme.palette.primary.main }}
                  startIcon={<Add />}
                  onClick={handleAddColorKey}
                >
                  Adicionar
                </Button>
              </Box>
              <ClickAwayListener
                onClickAway={() => setOpenColorOptionIndex(null)}
              >
                <Box>
                  {Array.isArray(columnData?.colorsByKey) &&
                    columnData.colorsByKey.map((colorKey, colorKeyIndex) => (
                      <Fragment key={colorKeyIndex}>
                        <KeyColor
                          type={columnData?.myColumnType}
                          onUpdateKeyColor={handleUpdateKeyColor}
                          onRemoveKeyColor={() =>
                            handleRemoveColorKey(colorKeyIndex)
                          }
                          colorKeyIndex={colorKeyIndex}
                          data={colorKey}
                          openColorOptionIndex={openColorOptionIndex}
                          setOpenColorOptionIndex={setOpenColorOptionIndex}
                          getAllColors={getAllColors}
                          getColorFromPallet={getColorFromPallet}
                        />
                      </Fragment>
                    ))}
                </Box>
              </ClickAwayListener>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default React.memo(DatagridColumns);
